// Studio HTTP calls. Hosts get a studio token with their Rumio session;
// guests get one from the public join endpoint. After that both talk to
// the studio with X-Studio-Token (guests have no Rumio session at all).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

export interface StudioSession {
  token: string;
  participantId: string;
  room: { id: string; name: string };
}

export interface StudioRoom {
  id: string;
  name: string;
  status: string;
  inviteUrl: string | null;
  createdAt: string;
}

export class StudioError extends Error {
  constructor(
    public status: number,
    public code: string | null,
  ) {
    super(code ?? `studio_${status}`);
  }
}

async function json<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new StudioError(res.status, typeof body.error === 'string' ? body.error : null);
  return body as T;
}

export async function studioFetch<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-Studio-Token', token);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  return json<T>(await fetch(path, { ...init, headers, credentials: 'omit' }));
}

/** Guests: a reload rejoins as the same participant instead of a new one. */
const guestKey = (inviteToken: string) => `studio:guest:${inviteToken}`;

export function savedGuest(inviteToken: string): StudioSession | null {
  try {
    const raw = sessionStorage.getItem(guestKey(inviteToken));
    return raw ? (JSON.parse(raw) as StudioSession) : null;
  } catch {
    return null;
  }
}

export async function joinAsGuest(inviteToken: string, displayName: string): Promise<StudioSession> {
  const session = await json<StudioSession>(
    await fetch('/api/studio/join', {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inviteToken, displayName }),
    }),
  );
  try {
    sessionStorage.setItem(guestKey(inviteToken), JSON.stringify(session));
  } catch {
    // Private mode: a reload just joins again.
  }
  return session;
}

export function forgetGuest(inviteToken: string): void {
  try {
    sessionStorage.removeItem(guestKey(inviteToken));
  } catch {
    // nothing stored
  }
}

export const hostSession = (roomId: string) => api<StudioSession>(`/api/studio/rooms/${roomId}/host-token`, { method: 'POST' });

export async function fetchIceServers(token: string): Promise<RTCIceServer[]> {
  return (await studioFetch<{ iceServers: RTCIceServer[] }>('/api/studio/ice', token)).iceServers;
}

export interface TakeUploads {
  session: { id: string; status: string; takeNumber: number };
  tracks: Array<{
    id: string;
    participantId: string;
    displayName: string;
    kind: 'audio' | 'video';
    segmentIndex: number;
    status: string;
    receivedChunks: number;
    bytes: number;
    finalSeq: number | null;
    pendingChunks: number | null;
  }>;
}

/** Host: everyone's upload progress for a take. */
export const fetchUploads = (sessionId: string, token: string) => studioFetch<TakeUploads>(`/api/studio/sessions/${sessionId}/uploads`, token);

export const GUEST_ERRORS: Record<string, string> = {
  invite_not_found: 'This invite link isn’t valid any more. Ask the host for a new one.',
  room_full: 'This studio is full.',
  room_closed: 'This recording has ended.',
  validation_failed: 'Enter your name to join.',
  studio_unavailable: 'The studio isn’t available right now.',
};

export function guestErrorMessage(err: unknown): string {
  const code = err instanceof StudioError ? err.code : null;
  if (err instanceof StudioError && err.status === 429) return 'Too many attempts. Wait a minute and try again.';
  return (code && GUEST_ERRORS[code]) ?? 'Couldn’t join the studio. Check your connection and try again.';
}

// ── Host room management (Rumio session) ──

export function useStudioRooms() {
  return useQuery<StudioRoom[]>({
    queryKey: ['studio-rooms'],
    queryFn: async () => (await api<{ rooms: StudioRoom[] }>('/api/video/rooms')).rooms,
  });
}

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      (await api<{ room: StudioRoom }>('/api/video/rooms', { method: 'POST', body: JSON.stringify({ name }) })).room,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio-rooms'] }),
  });
}

export interface Take {
  id: string;
  takeNumber: number;
  status: string;
  error: string | null;
  startedAt: string;
  durationMs: number | null;
  projectId: string | null;
  mixMediaFileId: string | null;
}

/** Takes still on their way to a mix. */
const UNSETTLED = new Set(['recording', 'stopped', 'assembling']);

/** A room's takes; refreshed while any is still on its way. */
export function useTakes(roomId: string, intervalMs = 10_000) {
  return useQuery<Take[]>({
    queryKey: ['studio-takes', roomId],
    queryFn: async () => (await api<{ takes: Take[] }>(`/api/studio/rooms/${roomId}/takes`)).takes,
    refetchInterval: (q) => (q.state.data?.some((t) => UNSETTLED.has(t.status)) ? intervalMs : false),
  });
}

export function useRotateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => api<{ inviteUrl: string }>(`/api/studio/rooms/${roomId}/invite/rotate`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio-rooms'] }),
  });
}
